import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface RndWidget {
  id: number;
  title: string;
  widget_type: string;
  data_source: string;
  refresh_seconds: number;
  created_at: string;
  chartOptions?: Highcharts.Options;
  kpiValue?: number | string;
  tableRows?: Array<{ label: string; value: unknown }>;
  loading: boolean;
  error: boolean;
}

@Component({
  selector: 'app-rnd-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, HighchartsChartModule],
  templateUrl: './rnd-dashboard.component.html',
  styleUrls: ['./rnd-dashboard.component.scss'],
})
export class RndDashboardComponent implements OnInit, OnDestroy {
  Highcharts = Highcharts;
  widgets: RndWidget[] = [];
  loading = true;
  private readonly refreshTimers: ReturnType<typeof setInterval>[] = [];

  constructor(private readonly http: HttpClient) {}

  ngOnInit(): void {
    void this.loadWidgets();
  }

  ngOnDestroy(): void {
    this.refreshTimers.forEach(t => clearInterval(t));
  }

  async loadWidgets(): Promise<void> {
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.apiBase}/widgets`)
      );
      const raw: any[] = res?.data ?? res ?? [];
      this.widgets = raw.map(w => ({
        id: w.id,
        title: w.title,
        widget_type: w.widget_type,
        data_source: w.data_source,
        refresh_seconds: w.refresh_seconds ?? 60,
        created_at: w.created_at,
        loading: true,
        error: false,
      }));
      await Promise.all(this.widgets.map(w => this.loadWidgetData(w)));
      this.widgets.forEach(w => this.scheduleRefresh(w));
    } catch {
      this.widgets = [];
    } finally {
      this.loading = false;
    }
  }

  async removeWidget(widget: RndWidget): Promise<void> {
    try {
      await firstValueFrom(
        this.http.delete<any>(`${this.apiBase}/widgets/${widget.id}`)
      );
      this.widgets = this.widgets.filter(w => w.id !== widget.id);
    } catch { /* ignore */ }
  }

  private async loadWidgetData(widget: RndWidget): Promise<void> {
    widget.loading = true;
    widget.error = false;
    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.apiBase}/widget-data?source=${widget.data_source}`)
      );
      const data: { labels: string[]; data: number[] } = res?.data ?? res;
      this.applyData(widget, data);
    } catch {
      widget.error = true;
    } finally {
      widget.loading = false;
    }
  }

  private applyData(widget: RndWidget, data: { labels: string[]; data: number[] }): void {
    if (widget.widget_type === 'KPI') {
      widget.kpiValue = data.data.reduce((a, b) => a + b, 0);
      return;
    }
    if (widget.widget_type === 'TABLE') {
      widget.tableRows = data.labels.map((label, i) => ({ label, value: data.data[i] }));
      return;
    }
    widget.chartOptions = this.buildChartOptions(widget, data);
  }

  private buildChartOptions(widget: RndWidget, data: { labels: string[]; data: number[] }): Highcharts.Options {
    const type = this.hcType(widget.widget_type);
    const isPie = type === 'pie';

    return {
      chart: { type, backgroundColor: 'transparent', height: 260 },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: isPie },
      xAxis: isPie ? undefined : { categories: data.labels, labels: { style: { fontSize: '11px' } } },
      yAxis: isPie ? undefined : { title: { text: null }, gridLineColor: '#f3f4f6' },
      tooltip: { shared: true },
      plotOptions: {
        series: { animation: { duration: 400 } },
        pie: { dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.percentage:.0f}%' } },
      },
      series: isPie
        ? [{ type: 'pie', name: widget.title, data: data.labels.map((name, i) => ({ name, y: data.data[i] })) }]
        : [{ type, name: widget.title, data: data.data, color: '#16a34a' } as Highcharts.SeriesOptionsType],
    };
  }

  private hcType(widgetType: string): string {
    switch (widgetType) {
      case 'BAR_CHART':  return 'bar';
      case 'LINE_CHART': return 'line';
      case 'PIE_CHART':  return 'pie';
      default:           return 'column';
    }
  }

  private scheduleRefresh(widget: RndWidget): void {
    const secs = Math.max(widget.refresh_seconds ?? 60, 30);
    const timer = setInterval(() => void this.loadWidgetData(widget), secs * 1000);
    this.refreshTimers.push(timer);
  }

  isChart(widget: RndWidget): boolean {
    return ['BAR_CHART', 'LINE_CHART', 'PIE_CHART', 'COLUMN_CHART'].includes(widget.widget_type);
  }

  private get apiBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/dashboard`;
  }
}
